import {
  Accordion,
  Section,
  SectionHeader,
  Skeleton,
} from "@apex/component-library";
import { Box, Button, Link, Text } from "@chakra-ui/react";
import { AxiosError } from "axios";
import { useState } from "react";
import type { QueryStatus } from "react-query";

import { AppRoutes } from "../../routes";
import { CallDetailResultFromAPI } from "../../types";
import EventItem from "./EventItem";

type Props = {
  hasAccountData?: boolean;
  entries?: CallDetailResultFromAPI[];
  apiStatus?: QueryStatus;
  accountDataStatus?: QueryStatus;
  accountId?: string;
  error?: AxiosError;
};

const MAX_DETAILS_ENTRIES = 25;

export default function OpenCallsAndAlertsDetails({
  hasAccountData,
  entries,
  apiStatus,
  accountDataStatus,
  accountId,
  error,
}: Props) {
  const [accordionItemIdx, setAccordionItemIdx] = useState<number[]>([]);

  const hasEntries = entries && entries.length > 0;
  const isShowingAccordions = accordionItemIdx.length !== 0;

  const errorStatus = error?.response?.status || error?.request.status;

  const toggleShowAll = () => {
    if (!isShowingAccordions && entries) {
      setAccordionItemIdx(entries.map((_, idx) => idx));
    } else {
      setAccordionItemIdx([]);
    }
  };

  const handleAccordionItemClick = (idx: number) => {
    if (accordionItemIdx.includes(idx)) {
      setAccordionItemIdx(accordionItemIdx.filter(i => i !== idx));
    } else {
      setAccordionItemIdx([...accordionItemIdx, idx]);
    }
  };

  const toolsExpandAll =
    hasAccountData && hasEntries ? (
      <Button
        variant="link"
        color="white"
        ml="auto"
        onClick={toggleShowAll}
        h="1rem"
        border="0"
        data-testid="toggle-btn"
      >
        {isShowingAccordions ? "Close All" : "Expand All"}
      </Button>
    ) : null;
  let errorText = "";
  if (apiStatus === "error") {
    switch (errorStatus) {
      case 403:
        errorText = "Additional permissions are required.";
        break;
      default:
        errorText =
          "There was a problem retrieving this information. Please try again later.";
        break;
    }
  }
  return (
    <Section>
      <SectionHeader tools={toolsExpandAll}>
        Open Calls & Alerts
        {accountId && hasAccountData && ` - ${accountId.toUpperCase()}`}
      </SectionHeader>
      {apiStatus === "loading" && (
        <Skeleton>
          <Box width="100%" height="50px"></Box>
        </Skeleton>
      )}
      {((!hasAccountData && apiStatus === "idle") ||
        (accountDataStatus === "error" && apiStatus !== "error")) && (
        <Text pl="3">Look-up an Account ID to see activity.</Text>
      )}
      {apiStatus === "success" && hasEntries && hasAccountData && (
        <Accordion index={accordionItemIdx} allowMultiple>
          {entries?.slice(0, MAX_DETAILS_ENTRIES).map((entry, index) => (
            <EventItem
              key={entry.eventId}
              entry={entry}
              entryIndex={index}
              handleClick={handleAccordionItemClick}
            />
          ))}
        </Accordion>
      )}
      {hasAccountData && !hasEntries && apiStatus === "success" && (
        <Text pl="3">No open entries for this account.</Text>
      )}
      {apiStatus === "success" && accountDataStatus === "success" && (
        <Text data-testid="entry-count-summary" pl="3" pt="3">
          Showing{" "}
          {entries && entries.length > MAX_DETAILS_ENTRIES
            ? `${MAX_DETAILS_ENTRIES}`
            : `${entries?.length}`}
          {` of ${entries?.length} | `}
          <Link
            data-testid="view-calls-alerts-link"
            href={`${window.location.origin}/${AppRoutes.Root}/${AppRoutes.CallsAlerts}?accountId=${accountId}&status=OPEN`}
            color="apex.link"
          >
            View Calls & Alerts
          </Link>
        </Text>
      )}
      {apiStatus === "error" && <Text pl="3">{errorText}</Text>}
    </Section>
  );
}
